import { JobPhotos } from '@/components/Jobs/JobPhotos'
// ─────────────────────────────────────────────────────────────────────────────
// PhotosModule — MIDNIGHT elevation
//
// §10 glass nesting: ModulePanel = ds-tile-inset (flat rgba, NO backdrop-filter).
//
// One "Add photos" button with a Before / During / After choice that defaults
// from the stage (UX-162). Phases with photos show a grid; empty phases fold
// into one line. Tap a thumbnail → lightbox.
//
// API endpoints:
//   GET    /api/jobs/:jobId/photos                 (jobId present)
//   GET    /api/deals/:dealId/photos               (dealId only)
//   POST   /api/jobs/:jobId/photos                 (upload)
//   POST   /api/deals/:dealId/photos               (upload)
//   DELETE /api/deals/:dealId/photos/:photoId      (deal photos only — jobs have no delete endpoint)
// ─────────────────────────────────────────────────────────────────────────────

import { useOnline } from '@/hooks/useOnline'
import { useNetworkAction } from '@/components/ui/OfflineActions'
import { DisabledReason } from '@/components/ui/FormField'
import { humanError } from '@/lib/errors'
import { useState, useEffect, useId, type ImgHTMLAttributes } from 'react'
import {
  Camera, Upload, X, ChevronLeft, ChevronRight, AlertCircle, ImageOff,
} from 'lucide-react'
import { useConfirm } from '@/components/ui/ConfirmDialog'
import { toast } from '@/lib/toast'
import { apiClient } from '@/lib/api'
import { resolveApiUrl } from '@/lib/apiUrl'
import type { ApiJobPhoto, ApiDealPhoto } from '@/lib/apiTypes'
import type { Record_, Stage } from './types'
import { SectionHeader, ModulePanel } from './_drawerHelpers'

// ─── which stages show the photos module ─────────────────────────────────────
const PHOTO_STAGES: Stage[] = [
  'LEAD', 'QUALIFIED', 'SCHEDULED', 'IN_PROGRESS', 'COMPLETED', 'INVOICED', 'PAID',
]

export function shouldShowPhotos(stage: Stage): boolean {
  return PHOTO_STAGES.includes(stage)
}

// ─── bucket type ─────────────────────────────────────────────────────────────
type PhotoBucket = 'BEFORE' | 'DURING' | 'AFTER'

const BUCKET_LABELS: Record<PhotoBucket, string> = {
  BEFORE: 'Before',
  DURING: 'During',
  AFTER:  'After',
}

// ─── Lightbox ─────────────────────────────────────────────────────────────────

interface LightboxProps {
  photos: ApiJobPhoto[]
  startIdx: number
  onClose: () => void
}

function Lightbox({ photos, startIdx, onClose }: LightboxProps) {
  const [idx, setIdx] = useState(startIdx)

  const prev = () => setIdx((i) => (i - 1 + photos.length) % photos.length)
  const next = () => setIdx((i) => (i + 1) % photos.length)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape')      onClose()
      if (e.key === 'ArrowLeft')   prev()
      if (e.key === 'ArrowRight')  next()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  })

  const photo = photos[idx]
  if (!photo) return null

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Photo lightbox"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 70,
        background: 'rgb(var(--bg-rgb) / 0.93)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={onClose}
    >
      {/* Close */}
      <button
        type="button"
        aria-label="Close lightbox"
        onClick={onClose}
        style={{
          position: 'absolute',
          top: 16,
          right: 16,
          width: 44,
          height: 44,
          borderRadius: 9999,
          background: 'rgb(var(--fg-rgb) / 0.10)',
          border: 'none',
          color: 'rgb(var(--fg-rgb) / 0.70)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          transition: 'background 0.2s',
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLButtonElement).style.background =
            'rgb(var(--fg-rgb) / 0.20)'
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLButtonElement).style.background =
            'rgb(var(--fg-rgb) / 0.10)'
        }}
      >
        <X style={{ width: 20, height: 20 }} />
      </button>

      {/* Prev / next */}
      {photos.length > 1 && (
        <>
          <button
            type="button"
            aria-label="Previous photo"
            onClick={(e) => { e.stopPropagation(); prev() }}
            style={{
              position: 'absolute',
              left: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              width: 44,
              height: 44,
              borderRadius: 9999,
              background: 'rgb(var(--fg-rgb) / 0.10)',
              border: 'none',
              color: 'rgb(var(--fg-rgb))',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'background 0.2s',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background =
                'rgb(var(--fg-rgb) / 0.20)'
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background =
                'rgb(var(--fg-rgb) / 0.10)'
            }}
          >
            <ChevronLeft style={{ width: 22, height: 22 }} />
          </button>
          <button
            type="button"
            aria-label="Next photo"
            onClick={(e) => { e.stopPropagation(); next() }}
            style={{
              position: 'absolute',
              right: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              width: 44,
              height: 44,
              borderRadius: 9999,
              background: 'rgb(var(--fg-rgb) / 0.10)',
              border: 'none',
              color: 'rgb(var(--fg-rgb))',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'background 0.2s',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background =
                'rgb(var(--fg-rgb) / 0.20)'
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background =
                'rgb(var(--fg-rgb) / 0.10)'
            }}
          >
            <ChevronRight style={{ width: 22, height: 22 }} />
          </button>
        </>
      )}

      {/* Counter */}
      <p
        style={{
          position: 'absolute',
          bottom: 20,
          left: '50%',
          transform: 'translateX(-50%)',
          fontSize: 13,
          color: 'var(--midnight-text3)',
          fontVariantNumeric: 'tabular-nums',
          letterSpacing: '-0.01em',
        }}
      >
        {idx + 1} / {photos.length}
      </p>

      {/* Image */}
      <PhotoImage
        src={resolveApiUrl(photo.photoUrl)}
        label={photoLabel(photo.type)}
        style={{
          maxWidth: '90vw',
          maxHeight: '85vh',
          objectFit: 'contain',
          borderRadius: 12,
        }}
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  )
}

const photoLabel = (type: string) => `${type.charAt(0)}${type.slice(1).toLowerCase()} photo`

// ─── PhotoImage ───────────────────────────────────────────────────────────────
// A photo whose file can't be fetched (moved, deleted, an unreachable host)
// shows a quiet "Photo unavailable" tile instead of a broken-image icon with
// "BEFORE" as its text.

export function PhotoImage({ src, label, style, ...rest }: { src: string; label: string } & Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'alt'>) {
  const [failed, setFailed] = useState(false)
  useEffect(() => { setFailed(false) }, [src])
  if (failed) {
    return (
      <span
        role="img"
        aria-label={`${label} (unavailable)`}
        data-testid="photo-unavailable"
        style={{
          ...style,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4,
          minWidth: 96, minHeight: 96, background: 'rgb(var(--fg-rgb) / 0.04)',
          color: 'var(--midnight-text3)', fontSize: 11, textAlign: 'center', padding: 6,
        }}
      >
        <ImageOff aria-hidden style={{ width: 18, height: 18 }} />
        Photo unavailable
      </span>
    )
  }
  return <img src={src} alt={label} style={style} onError={() => setFailed(true)} {...rest} />
}

// ─── ThumbnailGrid ────────────────────────────────────────────────────────────
// 3-col on mobile, 4-col on desktop (via CSS grid).
// Delete icon always visible (top-right, opacity-1) — no hover-only.

interface ThumbnailGridProps {
  photos: ApiJobPhoto[]
  allPhotos: ApiJobPhoto[]
  onThumbClick: (globalIdx: number) => void
  onDelete?: (photo: ApiJobPhoto) => void
}

function ThumbnailGrid({
  photos,
  allPhotos,
  onThumbClick,
  onDelete,
}: ThumbnailGridProps) {
  // Offline, delete is disabled and says why (UX-018).
  const net = useNetworkAction()
  return (
    <div className="grid grid-cols-3 gap-1.5">
      {photos.map((photo) => {
        const globalIdx = allPhotos.findIndex((p) => p.id === photo.id)
        return (
          <div
            key={photo.id}
            style={{ position: 'relative', aspectRatio: '1', borderRadius: 10, overflow: 'hidden' }}
          >
            <button
              type="button"
              aria-label={`View ${photo.type.toLowerCase()} photo`}
              onClick={() => onThumbClick(globalIdx)}
              style={{
                width: '100%',
                height: '100%',
                padding: 0,
                border: '0.5px solid rgb(var(--fg-rgb) / 0.08)',
                borderRadius: 10,
                overflow: 'hidden',
                cursor: 'pointer',
                background: 'none',
                display: 'block',
              }}
            >
              <PhotoImage
                src={resolveApiUrl(photo.photoUrl)}
                label={photoLabel(photo.type)}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                  transition: 'transform 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  (e.target as HTMLImageElement).style.transform = 'scale(1.04)'
                }}
                onMouseLeave={(e) => {
                  (e.target as HTMLImageElement).style.transform = 'scale(1)'
                }}
              />
            </button>

            {/* Delete — always visible, top-right */}
            {onDelete && (
              <button
                type="button"
                aria-label="Delete photo"
                {...net()}
                onClick={(e) => { e.stopPropagation(); onDelete(photo) }}
                style={{
                  position: 'absolute',
                  top: 2,
                  right: 2,
                  width: 32,
                  height: 32,
                  borderRadius: 9999,
                  background: 'rgb(var(--bg-rgb) / 0.65)',
                  border: '0.5px solid rgb(var(--fg-rgb) / 0.15)',
                  color: 'rgb(var(--fg-rgb) / 0.85)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  zIndex: 2,
                  // Always visible (opacity-1) — not hover-only
                  opacity: 1,
                  transition: 'background 0.15s',
                }}
                onMouseEnter={(e) => {
                  // Destructive hover: blush-deep, never a near-opaque pastel fill.
                  (e.currentTarget as HTMLButtonElement).style.background =
                    'rgba(81,22,34,0.90)'
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background =
                    'rgba(0,0,0,0.65)'
                }}
              >
                <X style={{ width: 11, height: 11 }} />
              </button>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ─── Add photos (one entry point — UX-162) ───────────────────────────────────

/** The phase a new photo most likely belongs to, from the record's stage. */
export function defaultPhotoBucket(stage: Stage): PhotoBucket {
  if (stage === 'IN_PROGRESS') return 'DURING'
  if (stage === 'COMPLETED' || stage === 'INVOICED' || stage === 'PAID') return 'AFTER'
  return 'BEFORE'
}

interface AddPhotosProps {
  stage: Stage
  entityId: string
  entityType: 'job' | 'deal'
  onUploaded: (photo: ApiJobPhoto | ApiDealPhoto) => void
  onDone?: () => void | Promise<void>
}

function AddPhotos({ stage, entityId, entityType, onUploaded, onDone }: AddPhotosProps) {
  const [bucket, setBucket] = useState<PhotoBucket>(() => defaultPhotoBucket(stage))
  const [uploading, setUploading] = useState(false)
  const inputId = useId()
  // Offline: an upload can't reach the server, so the button says why (UX-018).
  const online = useOnline()
  const blocked = uploading || !online

  useEffect(() => { setBucket(defaultPhotoBucket(stage)) }, [stage])

  const upload = async (files: FileList | null) => {
    if (!files || files.length === 0) return
    setUploading(true)
    let ok = 0
    try {
      const endpoint = entityType === 'deal' ? `/api/deals/${entityId}/photos` : `/api/jobs/${entityId}/photos`
      for (const file of Array.from(files)) {
        try {
          const fd = new FormData()
          fd.append(entityType === 'deal' ? 'file' : 'photo', file)
          fd.append('type', bucket)
          const res = await apiClient.post(endpoint, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
          onUploaded((res.data?.data ?? res.data) as ApiJobPhoto | ApiDealPhoto)
          ok++
        } catch (err) {
          toast.error(err, `upload ${file.name}`)
        }
      }
      if (ok > 0) {
        toast.success(`${ok} ${BUCKET_LABELS[bucket].toLowerCase()} photo${ok === 1 ? '' : 's'} added`)
        await onDone?.()
      }
    } finally {
      setUploading(false)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div role="radiogroup" aria-label="Photo phase" style={{ display: 'flex', gap: 6 }}>
        {(['BEFORE', 'DURING', 'AFTER'] as PhotoBucket[]).map((b) => (
          <button
            key={b}
            type="button"
            role="radio"
            aria-checked={bucket === b}
            onClick={() => setBucket(b)}
            style={{
              flex: 1,
              minHeight: 40,
              borderRadius: 9999,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              border: `0.5px solid ${bucket === b ? 'rgb(var(--fg-rgb) / 0.30)' : 'rgb(var(--fg-rgb) / 0.08)'}`,
              background: bucket === b ? 'rgb(var(--fg-rgb) / 0.12)' : 'transparent',
              color: bucket === b ? 'rgb(var(--fg-rgb))' : 'var(--midnight-text2)',
            }}
          >
            {BUCKET_LABELS[b]}
          </button>
        ))}
      </div>
      {/* A real, labelled file input; the label is the visible button. */}
      <input
        id={inputId}
        type="file"
        accept="image/*"
        multiple
        className="sr-only"
        disabled={blocked}
        onChange={(e) => { void upload(e.target.files); e.target.value = '' }}
      />
      <label
        htmlFor={inputId}
        className="ds-photo-add"
        aria-disabled={blocked || undefined}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          minHeight: 48,
          borderRadius: 9999,
          fontSize: 15,
          fontWeight: 600,
          color: 'rgb(var(--fg-rgb))',
          background: 'rgb(var(--fg-rgb) / 0.08)',
          border: '0.5px solid rgb(var(--fg-rgb) / 0.14)',
          cursor: uploading ? 'progress' : !online ? 'not-allowed' : 'pointer',
          opacity: !online ? 0.5 : 1,
        }}
      >
        <Upload style={{ width: 16, height: 16 }} aria-hidden />
        {uploading ? 'Uploading…' : `Add ${BUCKET_LABELS[bucket].toLowerCase()} photos`}
      </label>
      {!online && <DisabledReason className="mt-0 justify-center">You're offline. Add photos when you're back online.</DisabledReason>}
    </div>
  )
}

// ─── PhotosModule ─────────────────────────────────────────────────────────────

interface PhotosModuleProps {
  record: Record_
  /** Parent refetch after an upload (photo counts elsewhere). */
  onPhotoUploaded?: () => void | Promise<void>
}

export function PhotosModule(props: PhotosModuleProps) {
  return props.record.jobId
    ? <JobPhotos key={props.record.jobId} jobId={props.record.jobId} onPhotoUploaded={props.onPhotoUploaded} />
    : <LegacyPhotosModule {...props} />
}

function LegacyPhotosModule({ record, onPhotoUploaded }: PhotosModuleProps) {
  const [photos, setPhotos]         = useState<ApiJobPhoto[]>([])
  const [loading, setLoading]       = useState(false)
  const [fetchError, setFetchError] = useState<string | null>(null)
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null)
  const { confirm } = useConfirm()

  const jobId  = record.jobId
  const dealId = record.dealId

  const entityType: 'job' | 'deal' = jobId ? 'job' : 'deal'
  const entityId: string | undefined = jobId ?? dealId

  useEffect(() => {
    if (!entityId) return
    let cancelled = false
    setLoading(true)
    setFetchError(null)

    const endpoint =
      entityType === 'deal'
        ? `/api/deals/${entityId}/photos`
        : `/api/jobs/${entityId}/photos`

    apiClient
      .get(endpoint)
      .then((res) => {
        if (cancelled) return
        const data = res.data?.data ?? res.data
        setPhotos(Array.isArray(data) ? data : [])
      })
      .catch((err) => {
        if (cancelled) return
        const status = err?.response?.status ?? err?.status
        if (status === 404) {
          // 404 = no photos exist yet — treat as empty, not an error
          setPhotos([])
        } else {
          setFetchError(humanError(err, 'load the photos'))
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => { cancelled = true }
  }, [entityId, entityType])

  const handlePhotoUploaded = (photo: ApiJobPhoto | ApiDealPhoto) => {
    setPhotos((prev) => [...prev, photo as ApiJobPhoto])
  }

  // Only deal photos have a delete endpoint; job photos show no delete
  // button rather than one that only pretends (it used to hide the photo
  // locally and leave it on the server).
  const handleDeletePhoto = entityType === 'deal' && dealId
    ? async (photo: ApiJobPhoto) => {
        const ok = await confirm({
          title: 'Delete this photo?',
          impact: 'It will be removed from this lead for everyone.',
          confirmLabel: 'Delete photo',
          tone: 'danger',
        })
        if (!ok) return
        try {
          await apiClient.delete(`/api/deals/${dealId}/photos/${photo.id}`)
          setPhotos((prev) => prev.filter((p) => p.id !== photo.id))
          toast.success('Photo deleted')
        } catch (err) {
          toast.error(err, 'delete the photo')
        }
      }
    : undefined

  const byBucket: Record<PhotoBucket, ApiJobPhoto[]> = {
    BEFORE: photos.filter((p) => p.type === 'BEFORE'),
    DURING: photos.filter((p) => (p as { type: string }).type === 'DURING'),
    AFTER: photos.filter((p) => p.type === 'AFTER'),
  }
  const filled = (['BEFORE', 'DURING', 'AFTER'] as PhotoBucket[]).filter((b) => byBucket[b].length > 0)
  const empty = (['BEFORE', 'DURING', 'AFTER'] as PhotoBucket[]).filter((b) => byBucket[b].length === 0)

  if (!entityId) {
    return (
      <ModulePanel accent="neutral">
        <SectionHeader tone="neutral" icon={Camera} title="Photos" />
        <p style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13, color: 'var(--midnight-text3)', margin: 0 }}>
          <AlertCircle style={{ width: 14, height: 14, flexShrink: 0, marginTop: 1 }} aria-hidden />
          <span>Photos can be added once this has a job.</span>
        </p>
      </ModulePanel>
    )
  }

  return (
    <>
      <ModulePanel accent="neutral">
        <SectionHeader
          tone="neutral"
          icon={Camera}
          title={photos.length > 0 ? `Photos · ${photos.length}` : 'Photos'}
          meta={loading ? 'Loading…' : undefined}
        />

        {fetchError && (
          <p role="alert" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--blush-text)', margin: '0 0 12px' }}>
            <AlertCircle style={{ width: 13, height: 13, flexShrink: 0 }} aria-hidden />
            {fetchError}
          </p>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {filled.map((bucket) => (
            <div key={bucket}>
              <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--midnight-text2)', margin: '0 0 8px' }}>
                {BUCKET_LABELS[bucket]} <span style={{ color: 'var(--midnight-text3)', fontWeight: 500 }}>· {byBucket[bucket].length}</span>
              </p>
              <ThumbnailGrid
                photos={byBucket[bucket]}
                allPhotos={photos}
                onThumbClick={setLightboxIdx}
                onDelete={handleDeletePhoto}
              />
            </div>
          ))}

          {/* Empty phases fold into one line instead of three open dropzones. */}
          {/* Not after a failed load: "No photos yet" would be a guess. */}
          {!loading && !fetchError && empty.length > 0 && (
            <p style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--midnight-text3)', margin: 0 }}>
              <ImageOff style={{ width: 13, height: 13 }} aria-hidden />
              {empty.length === 3
                ? 'No photos yet'
                : `No ${empty.map((b) => BUCKET_LABELS[b].toLowerCase()).join(' or ')} photos yet`}
            </p>
          )}

          <AddPhotos
            stage={record.stage}
            entityId={entityId}
            entityType={entityType}
            onUploaded={handlePhotoUploaded}
            onDone={onPhotoUploaded}
          />
        </div>
      </ModulePanel>

      {lightboxIdx !== null && photos.length > 0 && (
        <Lightbox
          photos={photos}
          startIdx={lightboxIdx}
          onClose={() => setLightboxIdx(null)}
        />
      )}
    </>
  )
}
